import { useEffect, useState } from "react";
import Modal from "../../layout/Modal";
import Button from "../../layout/Button";
import TextArea from "../../layout/TextArea";
import Select from "../../layout/Select";
import useFormValidation from "../../../hooks/formValidation";
import { Queue } from "../../../model/queue.model";
import { WhatsappBotError, WhatsappBotMenuNode } from "../../../model/whatsappBot.model";

// Versão própria do bot de WhatsApp, bem mais simples que a da URA de voz
// (MenuNodeMessageModal.tsx em components/ivrs/menu): mensagem é texto
// puro, sem TTS/upload de áudio.
function MenuNodeMessageModal({
  shown,
  node,
  queues,
  onClose,
  onSave,
}: MenuNodeMessageModalProps) {
  const [message, setMessage] = useState("");
  const [customError, setCustomError] = useState(false);
  const [errorKind, setErrorKind] = useState<"text" | "queue">("text");
  const [errorText, setErrorText] = useState("");
  const [errorQueueId, setErrorQueueId] = useState("");

  const formValidation = useFormValidation();

  useEffect(() => {
    if (!shown) return;
    setMessage(node.message || "");
    setCustomError(!!node.error);
    if (node.error && typeof node.error === "object") {
      setErrorKind("queue");
      setErrorQueueId(node.error.queueId);
      setErrorText("");
    } else {
      setErrorKind("text");
      setErrorText(typeof node.error === "string" ? node.error : "");
      setErrorQueueId("");
    }
  }, [shown, node]);

  const saveHandler = () => {
    if (!formValidation.validateForm()) return;
    let error: WhatsappBotError | undefined;
    if (customError) {
      error = errorKind === "queue" ? { type: "queue", queueId: errorQueueId } : errorText;
    }
    onSave(message, error);
  };

  return (
    <Modal
      title="Mensagem deste nível"
      className="w-[560px]"
      shown={shown}
      onClose={onClose}
    >
      <div className="mb-6">
        <TextArea
          title="Mensagem de saudação"
          className="w-full"
          value={message}
          setter={setMessage}
          formValidation={formValidation}
          charLimit={1000}
          required
        />
        <p className="text-gray-dark text-xs mt-1">
          Enviada quando o contato chega neste nível. As opções abaixo dela
          já aparecem sozinhas na lista interativa do WhatsApp.
        </p>
      </div>

      <div className="mb-2">
        <label className="flex items-center gap-2 text-xs 2xl:text-sm font-bold text-gray-dark cursor-pointer">
          <input
            type="checkbox"
            checked={customError}
            onChange={(e) => setCustomError(e.target.checked)}
          />
          Personalizar mensagem de opção inválida neste nível
        </label>
        {!customError && (
          <div className="text-gray-dark text-xs mt-1">
            Sem personalização, este nível usa a mensagem de erro do nível
            anterior.
          </div>
        )}
      </div>

      {customError && (
        <div className="mb-6">
          <div className="flex rounded-10 border-2 border-gray/50 overflow-hidden mb-3">
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-bold ${
                errorKind === "text" ? "bg-white-dark text-primary" : "text-gray-dark"
              }`}
              onClick={() => setErrorKind("text")}
            >
              Mensagem de texto
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-bold border-l-2 border-gray/50 ${
                errorKind === "queue" ? "bg-white-dark text-primary" : "text-gray-dark"
              }`}
              onClick={() => setErrorKind("queue")}
            >
              Encaminhar direto pra fila
            </button>
          </div>

          {errorKind === "text" ? (
            <TextArea
              title="Mensagem de opção inválida"
              className="w-full"
              value={errorText}
              setter={setErrorText}
              formValidation={formValidation}
              charLimit={500}
              required
            />
          ) : (
            <Select
              title="Fila"
              className="w-full"
              value={errorQueueId}
              setter={setErrorQueueId}
              //@ts-ignore
              formValidation={formValidation}
              required
            >
              {queues.map((q, i) => (
                <option value={q.id} key={i}>
                  {q.name}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}

      <div>
        <Button color="purple" className="float-right" onClick={saveHandler}>
          Salvar
        </Button>
      </div>
    </Modal>
  );
}

export default MenuNodeMessageModal;

export type MenuNodeMessageModalProps = {
  shown: boolean;
  node: WhatsappBotMenuNode;
  queues: Queue[];
  onClose: () => void;
  onSave: (message: string, error: WhatsappBotError | undefined) => void;
};
